import { useCallback, useEffect, useMemo } from 'react';
import { Controller, useForm, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import c from 'classnames';
import { debounce } from 'lodash';
import { up } from 'styled-breakpoints';
import styled from 'styled-components';
import { match } from 'ts-pattern';
import { Language } from '@lgg/isomorphic/types/__generated__/graphql';
import { useActiveDepartmentSelectOptions } from 'src/components/domain/departments/hooks/use-active-department-select-options';
import { CompanyUsersSelect } from 'src/components/domain/users/components/company-users-select';
import { StackedCompanyUserList } from 'src/components/domain/users/components/stacked-company-user-list';
import { useSetFormFieldValue } from 'src/components/filters/helpers';
import { BaseButtonIcon, ButtonV2 } from 'src/components/general/button/lgg-button';
import { LggPopover } from 'src/components/general/display/popover';
import { BottomDrawer } from 'src/components/general/drawer/bottom/bottom-drawer';
import { preventDefaultAndStopPropagation } from 'src/components/general/inputs/helpers';
import { Radio } from 'src/components/general/inputs/radio';
import { Select, SelectOption } from 'src/components/general/inputs/select/select';
import { UnDecoratedTextInput } from 'src/components/general/inputs/text-input';
import { FlexColumn } from 'src/components/layout/flex-column';
import { FlexRow } from 'src/components/layout/flex-row';
import {
  TemplateLanguageValue,
  TemplateShareWithValue,
} from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/upsert-message-template-modal';
import {
  defaultFilterFormValues,
  filtersFormSchema,
  FiltersFormValues,
  OwnerFilterOption,
} from 'src/components/pages/conversations/components/general/lexical-editor/plugins/message-templates-plugin';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useCurrentUser } from 'src/hooks/use-current-user';
import { useVisible } from 'src/hooks/use-visible';

const InputContainer = styled(FlexColumn)`
  & > * {
    &:not(:last-child) {
      margin-bottom: 17px;
    }
  }
`;

const RadioContainer = styled(FlexColumn)`
  & > * {
    &:not(:last-child) {
      margin-bottom: 28px !important;
    }
  }
`;

const StyledRadio = styled(Radio)`
  font-size: 13px;
  line-height: 18px;
`;

const OwnerContainer = styled(FlexRow)`
  align-items: center;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.koala};
  color: ${({ theme }) => theme.colors.smalt};
  cursor: pointer;
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 13px;
  height: 38px;
  line-height: 16px;
  padding: 11px 10px;
`;

const PopoverOverlay = styled(FlexColumn)`
  padding: 20px 20px 15px;
  width: 295px;
`;

const OverlayTitle = styled.span`
  color: ${({ theme }) => theme.colors.carbon};
  display: none;
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 14px;
  font-style: normal;
  line-height: 18px;
  margin-bottom: 25px;

  ${up('md')} {
    display: block;
  }
`;

const FooterActionButton = styled(ButtonV2)`
  font-size: 12px;
  font-size: 14px;
  height: 38px;
  line-height: 18px;
  padding: 10px;
  width: 100%;

  ${up('md')} {
    font-size: 12px;
    height: 32px;
    padding: 7px 15px;
    width: unset;
  }

  & + & {
    margin-left: 15px;
  }
`;

const FooterActionButtonContainer = styled(FlexRow)`
  border-top: 1px solid ${({ theme }) => theme.colors.koala};
  justify-content: flex-end;
  margin-top: 15px;
  padding: 15px 20px;

  ${up('md')} {
    border: none;
    padding: 0;
  }
`;

const StyledCompanyUsersSelect = styled(CompanyUsersSelect)`
  padding-left: 30px;
  padding-top: 10px;
`;

const FilterInputContainer = styled(FlexRow)`
  align-items: center;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.koala};
  height: 34px;
  justify-content: space-between;
  padding: 0 10px;
  width: 100%;

  ${up('md')} {
    min-width: 203px;
  }
`;

const FilterTriggerIcon = styled(BaseButtonIcon)`
  margin: 0 0 0 10px !important;

  svg {
    height: 16px;
    margin: 0;
    width: 16px;

    path {
      fill: ${({ theme }) => theme.colors.flint};
    }
  }

  &:hover,
  &.active {
    svg path {
      fill: ${({ theme }) => theme.colors.smalt};
    }
  }
`;

const SearchTemplatesInput = styled(UnDecoratedTextInput)`
  input {
    font-size: 12px;
    line-height: 16px;

    height: 32px;
    padding: 0;

    &,
    &:focus,
    &:hover,
    &:active {
      border: none;
      box-shadow: none;
    }
  }
`;

type FooterActionButtonsProps = {
  onReset: React.MouseEventHandler<HTMLButtonElement>;
  onSubmit?: React.MouseEventHandler<HTMLButtonElement>;
  baseTestId?: string;
};

const FooterActionButtons = ({
  onReset,
  onSubmit,
  baseTestId,
}: FooterActionButtonsProps) => {
  const { t } = useTranslation(['common']);

  return (
    <FooterActionButtonContainer>
      <FooterActionButton
        variant="default"
        size="small"
        type="button"
        onClick={onReset}
        data-lgg-id={`${baseTestId}-reset-button`}
      >
        {t('common:filters.actionReset')}
      </FooterActionButton>
      <FooterActionButton
        variant="primary"
        type="submit"
        size="small"
        onClick={onSubmit}
        data-lgg-id={`${baseTestId}-apply-button`}
      >
        {t('common:filters.actionsApply')}
      </FooterActionButton>
    </FooterActionButtonContainer>
  );
};

const FieldLabel = styled.span`
  color: ${({ theme }) => theme.colors.raven};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 11px;
  letter-spacing: 0px;
  line-height: 14px;
  margin-bottom: 1px;
`;

const BottomDrawerContainer = styled(FlexColumn)`
  padding: 20px;
`;

type Props = {
  mainForm: UseFormReturn<FiltersFormValues>;
  onReset: VoidFunction;
  onChange: VoidFunction;
};

export const MessageTemplatesFilters = ({ mainForm, onReset, onChange }: Props) => {
  const { t } = useTranslation(['common', 'conversations']);
  const { loadingDepartmentOptions, departmentOptions } =
    useActiveDepartmentSelectOptions();
  const overlayVisibilityHandler = useVisible();
  const ownerFilterVisibilityHandler = useVisible();
  const currentUser = useCurrentUser();
  const breakpointUpMd = useBreakpoint(up('md'));

  const innerForm = useForm<FiltersFormValues>({
    resolver: zodResolver(filtersFormSchema),
    defaultValues: mainForm.getValues(),
  });

  const ownerForm = useForm<FiltersFormValues>({
    resolver: zodResolver(filtersFormSchema),
    defaultValues: mainForm.getValues(),
  });

  useEffect(() => {
    const watch = mainForm.watch((values) => {
      innerForm.reset(values);
      ownerForm.reset(values);
    });

    return () => watch.unsubscribe();
  }, [innerForm, mainForm, ownerForm]);

  const debouncedNameChangedHandler = useMemo(
    () =>
      debounce((name: string | undefined | null) => {
        mainForm.setValue('name', name, { shouldDirty: true });
      }, 400),
    [mainForm],
  );

  const { setFormFieldValue } = useSetFormFieldValue<FiltersFormValues>({
    defaultValues: mainForm.getValues(),
    setValue: innerForm.setValue,
  });

  const languageOptions: SelectOption<Language>[] = useMemo(
    () => [
      { label: t('common:languages.english'), value: TemplateLanguageValue.ENGLISH },
      { label: t('common:languages.spanish'), value: TemplateLanguageValue.SPANISH },
    ],
    [t],
  );

  const shareWithOptions: SelectOption<TemplateShareWithValue>[] = useMemo(
    () => [
      {
        label: t(
          'conversations:messageInput.options.messageTemplates.insertTemplatePlugin.modal.visibilityOptions.public',
        ),
        value: TemplateShareWithValue.EVERYONE,
      },
      {
        label: t(
          'conversations:messageInput.options.messageTemplates.insertTemplatePlugin.modal.visibilityOptions.onlyMe',
        ),
        value: TemplateShareWithValue.ONLY_ME,
      },
    ],
    [t],
  );

  const selectOwnerOption = useCallback(
    (value: OwnerFilterOption) => {
      ownerForm.setValue('owner', value);
      ownerForm.setValue('ownerList', undefined);
      ownerForm.clearErrors();
    },
    [ownerForm],
  );

  const ownerFieldSubmitHandler: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    async (e) => {
      preventDefaultAndStopPropagation(e);

      await ownerForm.handleSubmit(({ owner, ownerList = [] }) => {
        if (
          owner === OwnerFilterOption.SPECIFIC_PERSON &&
          (!ownerList || ownerList?.length <= 0)
        ) {
          ownerForm.setError('ownerList', { type: 'required' });

          return;
        }

        innerForm.setValue('owner', owner);
        innerForm.setValue('ownerList', ownerList);

        ownerFilterVisibilityHandler.close();
      })();
    },
    [innerForm, ownerFilterVisibilityHandler, ownerForm],
  );

  const ownerFieldFormContent = useMemo(
    () => (
      <Controller
        control={ownerForm.control}
        name="owner"
        render={({ field: ownerField }) => {
          return (
            <RadioContainer>
              <StyledRadio
                value={OwnerFilterOption.ANYONE}
                text={t(
                  'conversations:messageInput.options.messageTemplates.insertTemplatePlugin.modal.filters.fields.owner.options.anyone',
                )}
                name="message-template-filter-owner-radio-anyone"
                data-lgg-id="message-template-filter-owner-radio-anyone"
                onChange={() => {
                  selectOwnerOption(OwnerFilterOption.ANYONE);
                }}
                checked={ownerField.value === OwnerFilterOption.ANYONE}
              />
              <StyledRadio
                value={OwnerFilterOption.IM_THE_OWNER}
                text={t(
                  'conversations:messageInput.options.messageTemplates.insertTemplatePlugin.modal.filters.fields.owner.options.imTheOwner',
                )}
                name="message-template-filter-owner-radio-im-the-owner"
                data-lgg-id="message-template-filter-owner-radio-im-the-owner"
                onChange={() => {
                  selectOwnerOption(OwnerFilterOption.IM_THE_OWNER);
                }}
                checked={ownerField.value === OwnerFilterOption.IM_THE_OWNER}
              />
              <StyledRadio
                value={OwnerFilterOption.IM_NOT_THE_OWNER}
                text={t(
                  'conversations:messageInput.options.messageTemplates.insertTemplatePlugin.modal.filters.fields.owner.options.imNotTheOwner',
                )}
                name="message-template-filter-owner-radio-im-not-the-owner"
                data-lgg-id="message-template-filter-owner-radio-im-not-the-owner"
                onChange={() => {
                  selectOwnerOption(OwnerFilterOption.IM_NOT_THE_OWNER);
                }}
                checked={ownerField.value === OwnerFilterOption.IM_NOT_THE_OWNER}
              />
              <div>
                <StyledRadio
                  value={OwnerFilterOption.SPECIFIC_PERSON}
                  text={t(
                    'conversations:messageInput.options.messageTemplates.insertTemplatePlugin.modal.filters.fields.owner.options.specificPerson',
                  )}
                  name="message-template-filter-owner-radio-specific-person"
                  data-lgg-id="message-template-filter-owner-radio-specific-person"
                  onChange={() => {
                    selectOwnerOption(OwnerFilterOption.SPECIFIC_PERSON);
                  }}
                  checked={ownerField.value === OwnerFilterOption.SPECIFIC_PERSON}
                />
                <Controller
                  control={ownerForm.control}
                  name="ownerList"
                  render={({ field, fieldState }) => {
                    return (
                      <StyledCompanyUsersSelect
                        name="message-template-filter-owner-specific-person-list"
                        selectedUsersIds={
                          field.value ? field.value.map((val) => Number(val)) : []
                        }
                        isMulti
                        label=""
                        placeholder=""
                        onChange={(option) =>
                          ownerForm.setValue(
                            field.name,
                            (option as [])?.map(({ value }) => value),
                          )
                        }
                        isDisabled={
                          ownerField.value !== OwnerFilterOption.SPECIFIC_PERSON
                        }
                        hasError={Boolean(fieldState.error)}
                      />
                    );
                  }}
                />
              </div>
            </RadioContainer>
          );
        }}
      />
    ),
    [ownerForm, selectOwnerOption, t],
  );

  const ownerFieldTrigger = useMemo(
    () => (
      <FlexColumn>
        <FieldLabel>{t('common:owner')}</FieldLabel>
        <Controller
          control={innerForm.control}
          name="owner"
          render={({ field }) => {
            return (
              <OwnerContainer
                onClick={ownerFilterVisibilityHandler.show}
                data-lgg-id="message-template-filter-owner-overlay-trigger"
              >
                {field.value === OwnerFilterOption.SPECIFIC_PERSON ? (
                  <Controller
                    control={innerForm.control}
                    name="ownerList"
                    render={({ field }) => {
                      return (
                        <StackedCompanyUserList
                          selectedUsersIds={Array.from(field.value ?? [])}
                        />
                      );
                    }}
                  />
                ) : (
                  <>
                    {match(field.value)
                      .with(OwnerFilterOption.ANYONE, () =>
                        t(
                          'conversations:messageInput.options.messageTemplates.insertTemplatePlugin.modal.filters.fields.owner.options.anyone',
                        ),
                      )
                      .with(
                        OwnerFilterOption.IM_THE_OWNER,
                        () => `${currentUser.firstName} ${currentUser.lastName}`,
                      )
                      .with(OwnerFilterOption.IM_NOT_THE_OWNER, () =>
                        t(
                          'conversations:messageInput.options.messageTemplates.insertTemplatePlugin.modal.filters.fields.owner.options.imNotTheOwner',
                        ),
                      )
                      .otherwise(() => '')}
                  </>
                )}
              </OwnerContainer>
            );
          }}
        />
      </FlexColumn>
    ),
    [
      currentUser.firstName,
      currentUser.lastName,
      innerForm.control,
      ownerFilterVisibilityHandler.show,
      t,
    ],
  );

  const ownerFormFooterContent = useMemo(
    () => (
      <FooterActionButtons
        onReset={() => {
          innerForm.setValue('owner', OwnerFilterOption.ANYONE);
          innerForm.setValue('ownerList', undefined);
          ownerForm.setValue('owner', OwnerFilterOption.ANYONE);
          ownerForm.setValue('ownerList', undefined);

          ownerFilterVisibilityHandler.close();
        }}
        onSubmit={ownerFieldSubmitHandler}
        baseTestId="owner-overlay"
      />
    ),
    [innerForm, ownerFieldSubmitHandler, ownerFilterVisibilityHandler, ownerForm],
  );

  const formContent = useMemo(() => {
    return (
      <>
        <OverlayTitle>
          {t(
            'conversations:messageInput.options.messageTemplates.insertTemplatePlugin.modal.filters.title',
          )}
        </OverlayTitle>
        <InputContainer>
          {breakpointUpMd ? (
            <LggPopover
              visible={ownerFilterVisibilityHandler.visible}
              content={
                <PopoverOverlay data-lgg-id="message-template-filter-owner-overlay">
                  <OverlayTitle>{t('common:owner')}</OverlayTitle>
                  {ownerFieldFormContent}
                  {ownerFormFooterContent}
                </PopoverOverlay>
              }
              onVisibleChange={(isVisible) => {
                if (!isVisible) {
                  ownerForm.reset(innerForm.getValues());

                  ownerFilterVisibilityHandler.close();
                }
              }}
              hideArrow
              trigger={['click']}
              placement="bottomLeft"
              overlayStyle={{
                padding: 0,
              }}
            >
              {ownerFieldTrigger}
            </LggPopover>
          ) : (
            <>
              {ownerFieldTrigger}
              <BottomDrawer
                visible={ownerFilterVisibilityHandler.visible}
                title={t('common:owner')}
                fullHeight
                onClose={() => {
                  ownerForm.reset(innerForm.getValues());

                  ownerFilterVisibilityHandler.close();
                }}
                footer={ownerFormFooterContent}
              >
                <BottomDrawerContainer>{ownerFieldFormContent}</BottomDrawerContainer>
              </BottomDrawer>
            </>
          )}
          <Controller
            control={innerForm.control}
            name="department"
            render={({ field, fieldState }) => {
              const selectedOptions = departmentOptions.filter((option) =>
                field.value?.includes(option.value),
              );

              return (
                <Select
                  name="message-template-filter-department-input"
                  value={selectedOptions}
                  options={departmentOptions}
                  isLoading={loadingDepartmentOptions}
                  isSearchable={false}
                  isMulti
                  label={t('common:department')}
                  hasError={Boolean(fieldState.error)}
                  onChange={(option) =>
                    setFormFieldValue(
                      field.name,
                      option?.map(({ value }) => value),
                    )
                  }
                />
              );
            }}
          />
          <Controller
            control={innerForm.control}
            name="language"
            render={({ field, fieldState }) => {
              const selectedOptions = languageOptions.filter((option) =>
                field.value?.includes(option.value),
              );

              return (
                <Select
                  name="message-template-filter-language-input"
                  value={selectedOptions}
                  options={languageOptions}
                  isSearchable={false}
                  isMulti
                  hasError={Boolean(fieldState.error)}
                  label={t('common:language')}
                  onChange={(option) =>
                    setFormFieldValue(
                      field.name,
                      option?.map(({ value }) => value),
                    )
                  }
                />
              );
            }}
          />
          <Controller
            control={innerForm.control}
            name="shareWith"
            render={({ field, fieldState }) => {
              const selectedOptions = shareWithOptions.filter((option) =>
                field.value?.includes(option.value),
              );

              return (
                <Select
                  name="message-template-filter-visibility-input"
                  value={selectedOptions}
                  options={shareWithOptions}
                  isSearchable={false}
                  isMulti
                  hasError={Boolean(fieldState.error)}
                  label={t('common:visibility')}
                  onChange={(option) =>
                    setFormFieldValue(
                      field.name,
                      option?.map(({ value }) => value),
                    )
                  }
                />
              );
            }}
          />
        </InputContainer>
      </>
    );
  }, [
    breakpointUpMd,
    departmentOptions,
    innerForm,
    languageOptions,
    loadingDepartmentOptions,
    ownerFieldFormContent,
    ownerFieldTrigger,
    ownerFilterVisibilityHandler,
    ownerForm,
    ownerFormFooterContent,
    setFormFieldValue,
    shareWithOptions,
    t,
  ]);

  const filterContent = useMemo(
    () => (
      <FilterInputContainer>
        <Controller
          control={innerForm.control}
          name="name"
          render={({ field }) => {
            return (
              <SearchTemplatesInput
                {...field}
                placeholder={t(
                  'conversations:messageInput.options.messageTemplates.insertTemplatePlugin.modal.filters.fields.name.placeholder',
                )}
                autoComplete="off"
                data-lgg-id="contact-interaction-message-templates-filter-name"
                value={field.value ?? ''}
                onChange={(e) => {
                  field.onChange(e);

                  const value = e.target.value;
                  debouncedNameChangedHandler(value);
                }}
              />
            );
          }}
        />
        <FilterTriggerIcon
          type="filters"
          data-lgg-id="contact-interaction-message-templates-trigger-icon"
          onClick={overlayVisibilityHandler.show}
          className={c({
            active: overlayVisibilityHandler.visible,
          })}
          $notificationColor="monk"
          $showNotification={mainForm.formState.isDirty}
        />
      </FilterInputContainer>
    ),
    [
      debouncedNameChangedHandler,
      innerForm.control,
      mainForm.formState.isDirty,
      overlayVisibilityHandler.show,
      overlayVisibilityHandler.visible,
      t,
    ],
  );

  const submitHandler = useCallback(async () => {
    await innerForm.handleSubmit(async () => {
      const { language, department, owner, ownerList, shareWith } = innerForm.getValues();

      const setValueConfig = { shouldDirty: true };

      mainForm.setValue('language', language, setValueConfig);
      mainForm.setValue('department', department, setValueConfig);
      mainForm.setValue('owner', owner, setValueConfig);
      mainForm.setValue('ownerList', ownerList, setValueConfig);
      mainForm.setValue('shareWith', shareWith, setValueConfig);

      onChange();

      overlayVisibilityHandler.close();
    })();
  }, [innerForm, mainForm, onChange, overlayVisibilityHandler]);

  const resetAllFilters = useCallback(() => {
    mainForm.reset(defaultFilterFormValues);
    innerForm.reset(defaultFilterFormValues);
    ownerForm.reset(defaultFilterFormValues);
    onReset();

    overlayVisibilityHandler.close();
  }, [innerForm, mainForm, onReset, overlayVisibilityHandler, ownerForm]);

  const revertUnsavedChanges = useCallback(() => {
    const values = mainForm.getValues();

    innerForm.reset(values);
    overlayVisibilityHandler.close();
  }, [innerForm, mainForm, overlayVisibilityHandler]);

  return breakpointUpMd ? (
    <LggPopover
      visible={overlayVisibilityHandler.visible}
      content={
        <PopoverOverlay data-lgg-id="contact-interaction-message-templates-filter-overlay">
          {formContent}

          <FooterActionButtons
            onReset={resetAllFilters}
            onSubmit={submitHandler}
            baseTestId="message-template-filter-overlay"
          />
        </PopoverOverlay>
      }
      onVisibleChange={(isVisible) => {
        if (!isVisible) {
          revertUnsavedChanges();
        }
      }}
      overlayStyle={{
        padding: 0,
      }}
      hideArrow
      trigger={['click']}
      placement="bottomRight"
    >
      {filterContent}
    </LggPopover>
  ) : (
    <>
      {filterContent}
      <BottomDrawer
        visible={overlayVisibilityHandler.visible}
        title={t(
          'conversations:messageInput.options.messageTemplates.insertTemplatePlugin.modal.filters.title',
        )}
        fullHeight
        onClose={revertUnsavedChanges}
        footer={
          <FooterActionButtons onReset={resetAllFilters} onSubmit={submitHandler} />
        }
      >
        <BottomDrawerContainer>{formContent}</BottomDrawerContainer>
      </BottomDrawer>
    </>
  );
};
